// @flow
import * as React from 'react';
import { IntlContext, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { isEqual } from 'lodash';
import { withLeaflet } from 'react-leaflet';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import points14Messages from 'esm-web-pro/messages/points14Messages';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import DisconnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/DisconnectIcon';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import SplicedFiberRow from 'esm-web-pro/containers/pages/network/objects/point12/SplicedFiberRow';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { numberPointsCallbacksFiber } from 'esm-web-pro/containers/pages/network/objects/fiber/MapFiber';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import { Box } from 'grommet';
import { POINT12_CONTENT_TOOLTIP_KEY } from './CableList/Point12CableList';

type Props = {
    splice: Object,

    pointCoords: [number, number],
    openBreakSpliceForm: Function,
    map: Object,
    intl: IntlContext,
    circuit: Function,
    onView: Function,
    match: Object,
    history: Object,
    setClickedRowIdentity: Function,
    clickedRowIdentity: string,

    leaflet: LeafletContext,
    highlightFiber: Function,
    getFiberCableSeq: Function,
    disableSpliceBreak?: boolean,
    loading: boolean,
};

const SpliceRow = (props: Props) => {
    const {
        splice, intl: { formatMessage }, openBreakSpliceForm, map, intl, circuit,
        onView, setClickedRowIdentity, clickedRowIdentity, leaflet, highlightFiber, getFiberCableSeq,
        pointCoords, disableSpliceBreak, loading,
    } = props;
    let fibers = [...splice.fiber_starts, ...splice.fiber_ends];
    const actions = [];

    const handleBreak = () => {
        if (map.is_draft) {
            openBreakSpliceForm(splice);
        }
    };

    const getCurrentSpliceCircuit = () => circuit(splice.uuid, map);

    const getCircuit = (e) => {
        if (circuit) {
            getCurrentSpliceCircuit().then((res) => {
                const circuitPath = [];
                const fibersCallbacks = [];
                const fibersPointCallbacks = [];
                Object.keys(res.path).forEach((key, i) => {
                    let fiberPath = res.path[key].map((c) => [c[1], c[0]]);

                    if (!isEqual(pointCoords, fiberPath[0])) {
                        fiberPath = fiberPath.reverse();
                    }
                    const fiberCallback = highlightFiber(fiberPath, fibers.find((f) => f.id === Number(key)),
                        leaflet.map);

                    fibersCallbacks.push(fiberCallback);
                    circuitPath.push(...fiberPath);
                });

                fibers.forEach((fiber) => fibersPointCallbacks.push(...numberPointsCallbacksFiber(fiber)));
                onView(e, [
                    ...fibersCallbacks,
                    ...fibersPointCallbacks,
                ], circuitPath);
            });
        }
    };

    if (map.is_draft) {
        actions.push({
            key: `${splice.uuid}-break`,
            icon: <DisconnectIcon />,
            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleBreak();
            },
            size: '20px',
            width: '3px',
            tooltip: formatMessage(actionMessages.break),
            tooltipKey: POINT12_CONTENT_TOOLTIP_KEY,
            disabled: disableSpliceBreak || loading,
            loading,
        });
    }

    actions.push({
        key: `${splice.uuid}-more`,
        icon: <MeatballsIcon />,
        size: '20px',
        width: '3px',
        callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            const path = props.match.url.split('/layer');
            props.history.push(`${path[0]}/layer/splice/${splice.identity}`);
        },
        tooltip: intl.formatMessage(actionMessages.more),
        tooltipKey: POINT12_CONTENT_TOOLTIP_KEY,
    });

    const rowId = `S${splice.id}`;
    fibers = fibers.sort((f1, f2) => {
        const c1seq = getFiberCableSeq(f1.container);
        const c2seq = getFiberCableSeq(f2.container);

        if (c2seq > c1seq) {
            return -1;
        }
        if (c1seq > c2seq) {
            return 1;
        }
        return 0;
    });

    return (
        <Box pad={{ bottom: 'xsmall' }}>
            <ContentRow
                hoverIndicator="light-2"
                onClick={(e) => {
                    setClickedRowIdentity(rowId);
                    getCircuit(e);
                }}
                padLevel={1}
                key={splice.uuid}
                rowId={rowId}
                clickedRowIdentity={clickedRowIdentity}
                actions={(
                    <RowActions
                        iconPadding="3px"
                        gapBetweenActions="medium"
                        actions={actions}
                    />
                )}
            >
                <ValueText>
                    {`${formatMessage(points14Messages.splice)} ${splice.sequence}`}
                    {splice.is_factory ? ` (${formatMessage(points14Messages.isFactory)})` : ''}
                </ValueText>
            </ContentRow>
            {fibers.map((fiber) => (
                <SplicedFiberRow
                    key={fiber.uuid}
                    fiber={fiber}
                    getFiberCableSeq={getFiberCableSeq}
                    pointCoords={pointCoords}
                    onView={onView}
                    getCircuit={getCurrentSpliceCircuit}
                    setClickedRowIdentity={setClickedRowIdentity}
                    clickedRowIdentity={clickedRowIdentity}
                    highlightFiber={highlightFiber}
                />
            ))}
        </Box>
    );
};

SpliceRow.defaultProps = {
    disableSpliceBreak: false,
};

const apiCallback = (apiClient: ApiClient) => ({
    circuit: {
        send: (id: string, map: Object) => {
            if (map.is_draft) {
                return apiClient.resources.splice.circuit(id, { is_draft: map.uuid });
            }
            return apiClient.resources.splice.circuit(id);
        },
    },
});

export default withLeaflet(withRouter(injectIntl(connectAPI(apiCallback)(SpliceRow))));
